// ============================================================
// ReceptionDashboard Component
// ============================================================

import { Box } from '@mui/material';
import { Users, UserPlus, FileText, Search } from 'lucide-react';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { RecentActivityCard } from '@/components/dashboard/RecentActivityCard';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { AppPieChart } from '@/components/charts/AppPieChart';
import { WelcomeBanner } from '@/components/dashboard/WelcomeBanner';
import { SectionHeader } from '@/components/dashboard/SectionHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { tokens } from '@/theme/tokens';
import useDashboardStore from '@/store/dashboardStore';
import { AnalyticsFilter } from '@/components/filters/AnalyticsFilter';
import { useNavigate } from 'react-router-dom';

export function ReceptionDashboard({ stats, activities }) {
  const { user } = useAuth();
  const { currentSchool } = useSchool();
  const dateRange = useDashboardStore((state) => state.dateRange);
  const setDateRange = useDashboardStore((state) => state.setDateRange);
  const navigate = useNavigate();

  // Mock data for pie chart since it's reception
  const admissionSources = [
    { name: 'Walk-in', value: 45 },
    { name: 'Website', value: 30 },
    { name: 'Referral', value: 15 },
    { name: 'Other', value: 10 },
  ];

  return (
    <Box>
      <WelcomeBanner 
        user={user} 
        currentSchool={currentSchool} 
        action={<AnalyticsFilter value={dateRange} onChange={setDateRange} sx={{ bgcolor: 'background.paper', borderRadius: 1 }} />}
      />

      <SectionHeader title="Admissions & Requests" />
      <DashboardGrid columns={{ xs: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Students"
          value={stats.totalStudents?.toLocaleString()}
          icon={Users}
          color="primary"
          onClick={() => navigate('/students/list')}
        />
        <StatCard
          title="New Admissions"
          value={stats.newAdmissions?.toLocaleString()}
          icon={UserPlus}
          color="success"
          trend="up"
          trendValue="+5%"
        />
        <StatCard
          title="Recent Admissions"
          value={stats.todaysAdmissions?.toLocaleString()}
          icon={UserPlus}
          color="warning"
        />
        <StatCard
          title="Student Requests"
          value={stats.studentRequests?.toLocaleString()}
          icon={FileText}
          color="secondary"
        />
      </DashboardGrid>

      <DashboardGrid columns={{ xs: 1, lg: 3 }}>
        <Box sx={{ gridColumn: { lg: 'span 2' } }}>
          <RecentActivityCard activities={activities.filter(a => a.type === 'admission' || a.type === 'staff')} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ChartCard title="Admission Sources" height={250}>
            <AppPieChart 
              data={admissionSources} 
              colors={[tokens.colors.primary.main, tokens.colors.success.main, tokens.colors.warning.main, tokens.colors.secondary.main]} 
            />
          </ChartCard>
          
          <WidgetContainer title="Quick Actions">
            <Box sx={{ p: 1 }}>
              <DashboardGrid columns={2} gap={1}>
                <QuickActionCard title="Add Student" icon={UserPlus} color="primary" path="/students/add" />
                <QuickActionCard title="Search Student" icon={Search} color="secondary" path="/students/list" />
              </DashboardGrid>
            </Box>
          </WidgetContainer>
        </Box>
      </DashboardGrid>
    </Box>
  );
}
